import { JobStatus, NsnReport, PartFinderReport } from '@/api/generatedApi';
import { SectionHeader } from '@/components/AssetsPage/SectionHeader';
import { BodyText } from '@/components/BodyText';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { getBatchLabel } from '@/components/PartFinder/batchLabel';
import { useBatchReportSiblings } from '@/hooks/api/part-finder-batches/useBatchReportSiblings';
import { useGetPartFinderBatch } from '@/hooks/api/part-finder-batches/useGetPartFinderBatch';
import { useGetPartFinderReport } from '@/hooks/api/part-finder-reports/useGetPartFinderReport';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import {
  ChevronLeft,
  ChevronRight,
  DeleteOutlined,
  FactoryOutlined,
  StorefrontOutlined,
  SummarizeOutlined,
  TuneOutlined,
} from '@mui/icons-material';
import { Alert, Box, Chip, CircularProgress, IconButton, LinearProgress, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BulkDeleteReportsModal } from '@/components/PartFinder/BulkDeleteReportsModal';
import { PartFinderExportButton } from '@/components/PartFinder/PartFinderExportButton';
import { BatchBreadcrumbDropdown } from './components/BatchBreadcrumbDropdown';
import { CollapsibleSection } from './components/CollapsibleSection';
import { DetailsPanel } from './components/DetailsPanel';
import { FadeIn } from '@/components/PartFinder/FadeIn';
import { FinalRecommendationSection } from './components/FinalRecommendationSection';
import { ManufacturersTable } from './components/ManufacturersTable';
import { NsnSummarySkeleton } from './components/NsnSummarySkeleton';
import { NsnSummaryTable } from './components/NsnSummaryTable';
import { ImageSearchCandidatesSection } from './components/ImageSearchCandidatesSection';
import { OtherNsnsSection } from './components/OtherNsnsSection';
import { TechnicalCharacteristicsTable } from './components/TechnicalCharacteristicsTable';
import { SourcingOpportunitiesTable } from './components/SourcingOpportunitiesTable';
import { SourcingOpportunitiesSkeleton } from './components/SourcingOpportunitiesSkeleton';
import { IlsSearchSummaryLine } from './components/IlsSearchSummaryLine';
import { ProcurementOptionsTable } from './components/ProcurementOptionsTable';
import { ReportImages } from './components/ReportImages';
import { ReportDocuments } from './components/ReportDocuments';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  color: theme.palette.text.primary,
}));

const CONTENT_GUTTER = 6;
const LEFT_COLUMN_WIDTH = 270;

const ContentBox = styled(Box)({
  flex: 1,
  display: 'flex',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  gap: `${CONTENT_GUTTER}px`,
  padding: `${CONTENT_GUTTER}px`,
});

const LeftColumn = styled(Box)({
  width: `${LEFT_COLUMN_WIDTH}px`,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
});

const RightContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
});

const ContentWrapper = styled(Box)({
  paddingRight: '6px',
  overflowY: 'auto',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

const CONTENT_MAX_WIDTH = 1600;

const HEADER_MAX_WIDTH = CONTENT_MAX_WIDTH + LEFT_COLUMN_WIDTH + CONTENT_GUTTER * 2;

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  width: '100%',
  maxWidth: `${HEADER_MAX_WIDTH}px`,
  boxSizing: 'border-box',
  paddingLeft: `${CONTENT_GUTTER}px`,
});

const ReportSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: '12px',
  marginBottom: '16px',
  width: '100%',
  maxWidth: `${CONTENT_MAX_WIDTH}px`,
}));

const SubsectionHeader = styled(BodyText)(({ theme }) => ({
  display: 'block',
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: '0.9375rem',
  marginBottom: '8px',
}));

const GeneratingBar = styled(LinearProgress)(({ theme }) => ({
  width: '100%',
  maxWidth: `${CONTENT_MAX_WIDTH}px`,
  height: 6,
  borderRadius: 3,
  marginBottom: '16px',
  backgroundColor: theme.palette.action.hover,
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
  },
}));

const POLL_INTERVAL_MS = 3000;

const hasItems = <T,>(array: T[] | undefined): array is T[] => array !== undefined && array.length > 0;

const hasManufacturerRows = (nsnReport: NsnReport | undefined): nsnReport is NsnReport =>
  hasItems(nsnReport?.manufacturers) || hasItems(nsnReport?.nsnNowAlternates);

const isInProgress = (status: JobStatus | undefined): boolean =>
  status === JobStatus.PENDING || status === JobStatus.RUNNING;

const isNotInFlis = (report: PartFinderReport | undefined): boolean =>
  report?.status === JobStatus.COMPLETED && !report.nsnReport;

const TERMINAL_FAILURE_MESSAGE: Partial<Record<JobStatus, string>> = {
  [JobStatus.FAILED]: 'This report failed to generate. Try running the search again.',
  [JobStatus.CANCELLED]: 'This report was cancelled before it finished.',
};

export const PartFinderReportPage = () => {
  const showMockSections = false;
  const { id } = useParams<{ id: string }>();
  const {
    data: report,
    isLoading,
    error,
  } = useGetPartFinderReport(id || '', {
    refetchInterval: query => (isInProgress(query.state.data?.status) ? POLL_INTERVAL_MS : false),
  });
  const { getPath } = useTenants();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const displayIdentifier = report?.nsnReport?.searchedNsn ? report.nsnReport.nsn : report?.partNumber;
  usePageTitle(displayIdentifier ? `${displayIdentifier} - Part Finder Report` : 'Part Finder Report');

  const batchId = report?.batchId;
  const { data: batchData } = useGetPartFinderBatch(batchId ?? '');
  const { data: siblingsData } = useBatchReportSiblings(batchId);
  const batch = batchData?.partFinderBatch;
  const siblings = siblingsData?.partFinderReports ?? [];
  const currentIndex = siblings.findIndex(sibling => sibling.id === id);
  const previousReport = currentIndex > 0 ? siblings[currentIndex - 1] : undefined;
  const nextReport = currentIndex >= 0 && currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : undefined;
  const showPager = !!batchId && currentIndex >= 0 && siblings.length > 1;

  const header = (
    <SectionHeader>
      <HeaderRow>
        <Link to={getPath('part-finder')}>
          <BodyText>Part Finder</BodyText>
        </Link>
        {batchId && batch && (
          <>
            <ChevronRight sx={{ fontSize: '16px' }} />
            <BatchBreadcrumbDropdown
              batchId={batchId}
              label={getBatchLabel(batch)}
              siblings={siblings}
              currentReportId={id}
            />
          </>
        )}
        {report && (
          <>
            <ChevronRight sx={{ fontSize: '16px' }} />
            <BodyText>{displayIdentifier}</BodyText>
          </>
        )}
        {isNotInFlis(report) && (
          <Chip
            size='small'
            variant='outlined'
            label={report?.partNumberOnly ? 'Part number only' : 'Not in FLIS'}
            data-testid='not-in-flis-badge'
          />
        )}
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {report && <PartFinderExportButton reportIds={[report.id]} />}
          {report && (
            <Tooltip title='Delete report'>
              <IconButton size='small' color='error' aria-label='Delete report' onClick={() => setDeleteOpen(true)}>
                <DeleteOutlined fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          {showPager && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <IconButton
                size='small'
                aria-label='Previous report'
                disabled={!previousReport}
                onClick={() => {
                  if (previousReport) navigate(getPath(`part-finder-reports/${previousReport.id}`));
                }}
              >
                <ChevronLeft fontSize='small' />
              </IconButton>
              <BodyText sx={{ color: 'text.secondary' }}>
                {currentIndex + 1} of {siblings.length}
              </BodyText>
              <IconButton
                size='small'
                aria-label='Next report'
                disabled={!nextReport}
                onClick={() => {
                  if (nextReport) navigate(getPath(`part-finder-reports/${nextReport.id}`));
                }}
              >
                <ChevronRight fontSize='small' />
              </IconButton>
            </Box>
          )}
        </Box>
      </HeaderRow>
    </SectionHeader>
  );

  if (isLoading) {
    return (
      <PageContainer>
        {header}
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !report) {
    return (
      <PageContainer>
        {header}
        <ContentWrapper>
          <ApiErrorAlert error={getApiError(error, 'Failed to load report')} />
        </ContentWrapper>
      </PageContainer>
    );
  }

  const generating = isInProgress(report.status);
  const failureMessage = TERMINAL_FAILURE_MESSAGE[report.status];

  return (
    <PageContainer>
      {header}
      <ContentBox>
        <LeftColumn>
          <DetailsPanel report={report} />
        </LeftColumn>
        <RightContent>
          {generating && <GeneratingBar color='info' />}
          {isNotInFlis(report) && (
            <Alert severity='info' sx={{ marginBottom: '16px', maxWidth: `${CONTENT_MAX_WIDTH}px` }}>
              No catalog data. Sources: ILS (part number), web search.
            </Alert>
          )}
          {failureMessage && (
            <Alert
              severity={report.status === JobStatus.FAILED ? 'error' : 'warning'}
              sx={{ marginBottom: '16px', maxWidth: `${CONTENT_MAX_WIDTH}px` }}
            >
              {failureMessage}
            </Alert>
          )}

          <OtherNsnsSection report={report} />
          <ImageSearchCandidatesSection report={report} />

          <FinalRecommendationSection report={report} isGenerating={generating} />

          {(generating || (failureMessage ? hasItems(report.sourcingOpportunities) : report.sourcingOpportunities)) && (
            <CollapsibleSection
              icon={<StorefrontOutlined />}
              title='Sourcing Opportunities'
              tooltip='These sourcing opportunities are AI-generated suggestions. Please conduct your own due diligence to verify supplier details, pricing, and availability before making procurement decisions.'
            >
              {generating ? (
                <FadeIn key='sourcing-skeleton'>
                  <SourcingOpportunitiesSkeleton />
                </FadeIn>
              ) : (
                <FadeIn key='sourcing-loaded'>
                  <SourcingOpportunitiesTable reportId={report.id} opportunities={report.sourcingOpportunities ?? []} />
                  <IlsSearchSummaryLine summary={report.ilsSearch} nsnReport={report.nsnReport} />
                </FadeIn>
              )}
            </CollapsibleSection>
          )}

          {showMockSections && (
            <ReportSection>
              <SubsectionHeader>Procurement Options</SubsectionHeader>
              <ProcurementOptionsTable />
            </ReportSection>
          )}

          <ReportImages report={report} />

          {!generating && !failureMessage && <ReportDocuments report={report} />}

          {(generating || (!failureMessage && report.nsnReport)) && (
            <CollapsibleSection icon={<SummarizeOutlined />} title='Summary'>
              {report.nsnReport ? (
                <FadeIn key='summary-loaded'>
                  <NsnSummaryTable nsnReport={report.nsnReport} />
                </FadeIn>
              ) : (
                <FadeIn key='summary-skeleton'>
                  <NsnSummarySkeleton />
                </FadeIn>
              )}
            </CollapsibleSection>
          )}

          {hasManufacturerRows(report.nsnReport) && (
            <CollapsibleSection icon={<FactoryOutlined />} title='Manufacturers' defaultExpanded={false}>
              <FadeIn>
                <ManufacturersTable
                  manufacturers={report.nsnReport.manufacturers}
                  nsnNowAlternates={report.nsnReport.nsnNowAlternates}
                />
              </FadeIn>
            </CollapsibleSection>
          )}

          {hasItems(report.nsnReport?.technicalCharacteristics) && (
            <CollapsibleSection icon={<TuneOutlined />} title='Technical Characteristics' defaultExpanded={false}>
              <FadeIn>
                <TechnicalCharacteristicsTable technicalCharacteristics={report.nsnReport.technicalCharacteristics} />
              </FadeIn>
            </CollapsibleSection>
          )}
        </RightContent>
      </ContentBox>
      <BulkDeleteReportsModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        selectedReportIds={[report.id]}
        onDeleteComplete={() => {
          navigate(getPath(report.batchId ? `part-finder/batches/${report.batchId}` : 'part-finder'));
        }}
      />
    </PageContainer>
  );
};
